/**
 * Service centralisé pour tous les libellés d'interface
 * Assure une cohérence parfaite des termes français dans toute l'application
 */

// Types pour les actions
export type ActionType = 
  | 'save' | 'create' | 'update' | 'delete' | 'cancel' | 'close' | 'submit' 
  | 'add' | 'remove' | 'edit' | 'duplicate' | 'export' | 'import'
  | 'show' | 'hide' | 'toggle' | 'reorder' | 'upload' | 'download'
  | 'validate' | 'reset' | 'clear' | 'select' | 'deselect' | 'confirm'
  | 'view' | 'back' | 'next' | 'previous' | 'refresh' | 'filter' | 'search';

export type ButtonAction = ActionType;

export type StatusType = 
  | 'visible' | 'hidden' | 'active' | 'inactive' | 'enabled' | 'disabled'
  | 'pending' | 'completed' | 'cancelled' | 'draft' | 'published' | 'archived'
  | 'error' | 'success' | 'warning';

export type VariantType = 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'info' | 'neutral';

// Interface pour la configuration des labels
export interface LabelConfig {
  text: string;
  icon?: string;
  variant?: VariantType;
  tooltip?: string;
  loadingText?: string;
}

export interface ButtonLabelOptions {
  entityName?: string;
  isLoading?: boolean;
  count?: number;
  isEditing?: boolean;
}

/**
 * Service principal pour la gestion des labels
 */
export class LabelService {
  
  // Actions principales (Français)
  static readonly ACTIONS: Record<ActionType, LabelConfig> = {
    // Actions de sauvegarde et création
    save: { text: 'Enregistrer', icon: '💾', variant: 'primary', tooltip: 'Sauvegarder les modifications', loadingText: 'Sauvegarde...' },
    create: { text: 'Créer', icon: '➕', variant: 'success', tooltip: 'Créer un nouvel élément', loadingText: 'Création...' },
    update: { text: 'Modifier', icon: '✏️', variant: 'primary', tooltip: 'Mettre à jour les informations', loadingText: 'Mise à jour...' },
    submit: { text: 'Valider', icon: '✅', variant: 'primary', tooltip: 'Confirmer et soumettre', loadingText: 'Envoi...' },
    confirm: { text: 'Confirmer', icon: '✔️', variant: 'primary', tooltip: 'Confirmer cette action' },
    
    // Actions de suppression et retrait
    delete: { text: 'Supprimer', icon: '🗑️', variant: 'danger', tooltip: 'Supprimer définitivement', loadingText: 'Suppression...' },
    remove: { text: 'Retirer', icon: '➖', variant: 'warning', tooltip: 'Retirer de la liste' },
    
    // Actions de contrôle et navigation
    cancel: { text: 'Annuler', icon: '✖️', variant: 'secondary', tooltip: 'Annuler et fermer' },
    close: { text: 'Fermer', icon: '❌', variant: 'secondary', tooltip: 'Fermer cette fenêtre' },
    back: { text: 'Retour', icon: '◀️', variant: 'secondary', tooltip: 'Revenir en arrière' },
    next: { text: 'Suivant', icon: '▶️', variant: 'primary', tooltip: 'Étape suivante' },
    previous: { text: 'Précédent', icon: '◀️', variant: 'secondary', tooltip: 'Étape précédente' },
    
    // Actions d'ajout et édition
    add: { text: 'Ajouter', icon: '📝', variant: 'primary', tooltip: 'Ajouter un nouvel élément' },
    edit: { text: 'Éditer', icon: '🖊️', variant: 'info', tooltip: 'Modifier cet élément' },
    duplicate: { text: 'Dupliquer', icon: '📋', variant: 'info', tooltip: 'Créer une copie', loadingText: 'Duplication...' },
    view: { text: 'Voir', icon: '👁️', variant: 'secondary', tooltip: 'Afficher les détails' },
    
    // Actions d'import/export et fichiers
    export: { text: 'Exporter', icon: '📤', variant: 'info', tooltip: 'Exporter les données', loadingText: 'Exportation...' },
    import: { text: 'Importer', icon: '📥', variant: 'info', tooltip: 'Importer des données', loadingText: 'Importation...' },
    upload: { text: 'Téléverser', icon: '⬆️', variant: 'primary', tooltip: 'Envoyer un fichier', loadingText: 'Téléversement...' },
    download: { text: 'Télécharger', icon: '⬇️', variant: 'info', tooltip: 'Télécharger le fichier', loadingText: 'Téléchargement...' },
    
    // Actions de visibilité
    show: { text: 'Afficher', icon: '👁️', variant: 'primary', tooltip: 'Rendre visible' },
    hide: { text: 'Masquer', icon: '🙈', variant: 'secondary', tooltip: 'Cacher cet élément' },
    toggle: { text: 'Basculer', icon: '🔄', variant: 'info', tooltip: 'Changer la visibilité' },
    
    // Actions d'organisation et recherche
    reorder: { text: 'Réorganiser', icon: '🔃', variant: 'info', tooltip: 'Changer l\'ordre' },
    select: { text: 'Sélectionner', icon: '☑️', variant: 'primary', tooltip: 'Choisir cet élément' },
    deselect: { text: 'Désélectionner', icon: '☐', variant: 'secondary', tooltip: 'Retirer la sélection' },
    filter: { text: 'Filtrer', icon: '🔽', variant: 'secondary', tooltip: 'Filtrer les résultats' },
    search: { text: 'Rechercher', icon: '🔍', variant: 'primary', tooltip: 'Lancer la recherche' },
    
    // Actions de réinitialisation et nettoyage
    validate: { text: 'Valider', icon: '✔️', variant: 'success', tooltip: 'Confirmer la validation' },
    reset: { text: 'Réinitialiser', icon: '🔄', variant: 'warning', tooltip: 'Remettre à zéro' },
    clear: { text: 'Effacer', icon: '🧹', variant: 'warning', tooltip: 'Vider le contenu' },
    refresh: { text: 'Actualiser', icon: '🔄', variant: 'secondary', tooltip: 'Recharger les données', loadingText: 'Actualisation...' },
  };

  // Libellés de statut (Français)
  static readonly STATUS: Record<StatusType, LabelConfig> = {
    visible: { text: 'Visible', icon: '👁️', variant: 'success', tooltip: 'Élément affiché publiquement' },
    hidden: { text: 'Masqué', icon: '🙈', variant: 'secondary', tooltip: 'Élément non visible' },
    active: { text: 'Actif', icon: '🟢', variant: 'success', tooltip: 'Élément actif' },
    inactive: { text: 'Inactif', icon: '🔴', variant: 'danger', tooltip: 'Élément désactivé' },
    enabled: { text: 'Activé', icon: '✅', variant: 'success', tooltip: 'Fonctionnalité activée' },
    disabled: { text: 'Désactivé', icon: '❌', variant: 'danger', tooltip: 'Fonctionnalité désactivée' },
    pending: { text: 'En attente', icon: '🟡', variant: 'warning', tooltip: 'Action ou état en attente' },
    completed: { text: 'Terminé', icon: '✅', variant: 'success', tooltip: 'Action terminée avec succès' },
    cancelled: { text: 'Annulé', icon: '❌', variant: 'danger', tooltip: 'Action ou état annulé' },
    draft: { text: 'Brouillon', icon: '📝', variant: 'neutral', tooltip: 'En cours de rédaction' },
    published: { text: 'Publié', icon: '🌐', variant: 'success', tooltip: 'Visible par tout le monde' },
    archived: { text: 'Archivé', icon: '📦', variant: 'neutral', tooltip: 'Placé dans les archives' },
    error: { text: 'Erreur', icon: '❗', variant: 'danger', tooltip: 'Une erreur s\'est produite' },
    success: { text: 'Succès', icon: '✅', variant: 'success', tooltip: 'Opération réussie' },
    warning: { text: 'Attention', icon: '⚠️', variant: 'warning', tooltip: 'Point de vigilance' },
  };

  // Messages d'état de chargement
  static readonly LOADING = {
    default: 'Chargement en cours...',
    saving: 'Enregistrement en cours...',
    loading: 'Chargement des données...',
    uploading: 'Téléversement en cours...',
    processing: 'Traitement en cours...',
    deleting: 'Suppression en cours...',
    updating: 'Mise à jour en cours...',
  };

  // Messages de confirmation
  static readonly CONFIRMATION = {
    delete: 'Êtes-vous sûr de vouloir supprimer cet élément ?',
    deleteMultiple: 'Êtes-vous sûr de vouloir supprimer ces éléments ?',
    unsavedChanges: 'Vous avez des modifications non sauvegardées. Voulez-vous continuer ?',
    reset: 'Êtes-vous sûr de vouloir réinitialiser ?',
    clear: 'Êtes-vous sûr de vouloir effacer tout le contenu ?',
    overwrite: 'Un élément existe déjà. Voulez-vous le remplacer ?',
    cancel: 'Voulez-vous vraiment annuler les modifications ?',
    logout: 'Voulez-vous vous déconnecter ?',
    save: 'Voulez-vous sauvegarder les modifications ?',
  };

  // Messages d'erreur standards
  static readonly ERRORS = {
    required: (field: string) => `Le champ "${field}" est obligatoire`,
    invalid: (field: string) => `Le champ "${field}" n'est pas valide`,
    tooShort: (field: string, min: number) => `Le champ "${field}" doit contenir au moins ${min} caractères`,
    tooLong: (field: string, max: number) => `Le champ "${field}" ne peut pas dépasser ${max} caractères`,
    invalidFormat: (field: string) => `Le format du champ "${field}" n'est pas correct`,
    fileSize: (max: string) => `Le fichier ne doit pas dépasser ${max}`,
    fileType: (types: string[]) => `Types de fichiers autorisés : ${types.join(', ')}`,
    network: 'Erreur de connexion au serveur',
    unauthorized: 'Vous n\'êtes pas autorisé à effectuer cette action',
    notFound: 'Élément introuvable',
    serverError: 'Erreur interne du serveur',
    conflict: 'Conflit de données',
    generic: 'Une erreur est survenue',
  };

  // Placeholders pour les champs de formulaire
  static readonly PLACEHOLDERS = {
    search: 'Rechercher...',
    name: 'Nom',
    title: 'Titre',
    description: 'Description',
    email: 'Adresse e-mail',
    password: 'Mot de passe',
    confirmPassword: 'Confirmer le mot de passe',
    url: 'URL (https://...)',
    date: 'jj/mm/aaaa',
    dateTime: 'jj/mm/aaaa hh:mm',
    phone: 'Numéro de téléphone',
    file: 'Choisir un fichier...',
    optional: (field: string) => `${field} (optionnel)`,
  };

  // Libellés pour les entités métier
  static readonly ENTITIES = {
    project: { singular: 'Projet', plural: 'Projets', icon: '📁' },
    experience: { singular: 'Expérience', plural: 'Expériences', icon: '💼' },
    education: { singular: 'Formation', plural: 'Formations', icon: '🎓' },
    skill: { singular: 'Compétence', plural: 'Compétences', icon: '🛠️' },
    technology: { singular: 'Technologie', plural: 'Technologies', icon: '⚙️' },
    service: { singular: 'Service', plural: 'Services', icon: '🔧' },
    quote: { singular: 'Citation', plural: 'Citations', icon: '💬' },
    image: { singular: 'Image', plural: 'Images', icon: '🖼️' },
    document: { singular: 'Document', plural: 'Documents', icon: '📄' },
    user: { singular: 'Utilisateur', plural: 'Utilisateurs', icon: '👤' },
    setting: { singular: 'Paramètre', plural: 'Paramètres', icon: '⚙️' },
  };

  // Navigation et menus
  static readonly NAVIGATION = {
    dashboard: 'Tableau de bord',
    profile: 'Profil',
    settings: 'Paramètres',
    logout: 'Déconnexion',
    login: 'Connexion',
    register: 'Inscription',
    home: 'Accueil',
    about: 'À propos',
    contact: 'Contact',
    portfolio: 'Portfolio',
    blog: 'Blog',
    admin: 'Administration',
  };

  /**
   * Obtient le libellé pour une action
   */
  static getAction(action: ActionType, withIcon = false): string {
    const config = this.ACTIONS[action];
    if (!config) return action;
    return withIcon && config.icon ? `${config.icon} ${config.text}` : config.text;
  }

  /**
   * Obtient le libellé pour un statut
   */
  static getStatus(status: StatusType, withIcon = false): string {
    const config = this.STATUS[status];
    if (!config) return status;
    return withIcon && config.icon ? `${config.icon} ${config.text}` : config.text;
  }

  /**
   * Obtient la configuration complète pour une action
   */
  static getActionConfig(action: ActionType): LabelConfig {
    return this.ACTIONS[action];
  }

  /**
   * Obtient la configuration complète pour un statut
   */
  static getStatusConfig(status: StatusType): LabelConfig {
    return this.STATUS[status];
  }

  /**
   * Obtient l'icône associée à une action
   */
  static getButtonIcon(action: ActionType): string {
    return this.ACTIONS[action]?.icon || '🔲';
  }

  /**
   * Obtient les informations de statut complètes (icon, text, color)
   */
  static getStatusInfo(status: StatusType) {
    const config = this.STATUS[status];
    if (!config) return { icon: '❓', text: status, color: 'neutral' };
    
    // Rétrocompatibilité variant -> color
    let color = 'neutral';
    if (config.variant === 'danger') color = 'error';
    else if (config.variant === 'success') color = 'success';
    else if (config.variant === 'warning') color = 'warning';
    else if (config.variant === 'info') color = 'info';
    
    return {
      icon: config.icon || '❓',
      text: config.text,
      color
    };
  }

  /**
   * Obtient un message d'erreur à partir de son type
   */
  static getErrorMessage(errorType: keyof typeof this.ERRORS | string): string {
    if (errorType in this.ERRORS) {
      const val = this.ERRORS[errorType as keyof typeof this.ERRORS];
      return typeof val === 'function' ? val('élément') : val;
    }
    return this.ERRORS.generic;
  }

  /**
   * Génère un message de confirmation personnalisé
   */
  static getConfirmation(
    action: ActionType, 
    entityName?: string, 
    count = 1
  ): string {
    const actionText = this.getAction(action).toLowerCase();
    
    if (action === 'delete') {
      if (count > 1) {
        return `Êtes-vous sûr de vouloir ${actionText} ces ${count} éléments${entityName ? ` (${entityName})` : ''} ?`;
      }
      return `Êtes-vous sûr de vouloir ${actionText} cet élément${entityName ? ` (${entityName})` : ''} ?`;
    }
    
    return `Êtes-vous sûr de vouloir ${actionText}${entityName ? ` ce ${entityName.toLowerCase()}` : ' cet élément'} ?`;
  }

  /**
   * Obtient un message de confirmation basé sur le type ou l'action
   */
  static getConfirmationMessage(action: keyof typeof this.CONFIRMATION | string, entityName?: string): string {
    if (action in this.CONFIRMATION) {
      return this.CONFIRMATION[action as keyof typeof this.CONFIRMATION];
    }
    return this.getConfirmation(action as ActionType, entityName);
  }

  /**
   * Génère un libellé de bouton contextuel
   */
  static getButtonLabel(
    action: ActionType,
    context?: ButtonLabelOptions
  ): string {
    const { isEditing = false, isLoading = false, entityName, count = 1 } = context || {};
    const actionConfig = this.ACTIONS[action];
    if (!actionConfig) return action;
    
    if (isLoading) {
      if (actionConfig.loadingText) {
        return actionConfig.loadingText;
      }
      switch (action) {
        case 'save':
        case 'create':
        case 'update':
          return this.LOADING.saving;
        case 'delete':
          return this.LOADING.deleting;
        case 'upload':
          return this.LOADING.uploading;
        default:
          return this.LOADING.processing;
      }
    }

    // Contexte d'édition pour save
    if (action === 'save' && isEditing) {
      return this.getAction('update');
    }
    if (action === 'save' && !isEditing) {
      return this.getAction('create');
    }

    // Contexte avec entité
    if (entityName) {
      switch (action) {
        case 'create':
        case 'add':
          return `${this.getAction(action)} ${entityName.toLowerCase()}`;
        case 'delete':
          return `${this.getAction('delete')} ${entityName.toLowerCase()}`;
        case 'edit':
          return `${this.getAction('edit')} ${entityName.toLowerCase()}`;
        case 'save':
          return `${this.getAction('save')} ${entityName.toLowerCase()}`;
        case 'duplicate':
          return `${this.getAction('duplicate')} ${entityName.toLowerCase()}`;
      }
    }

    // Contexte avec compteur
    if (count > 1 && action === 'delete') {
      return `${this.getAction(action)} (${count})`;
    }

    return this.getAction(action);
  }

  /**
   * Génère un titre de modal avec contexte
   */
  static getModalTitle(
    action: ActionType,
    entityName?: string,
    isEditing = false
  ): string {
    const entity = entityName || 'élément';
    
    switch (action) {
      case 'create':
      case 'add':
        return `${this.getAction('create')} ${entity.toLowerCase()}`;
      case 'edit':
      case 'update':
        return `${this.getAction('edit')} ${entity.toLowerCase()}`;
      case 'delete':
        return `${this.getAction('delete')} ${entity.toLowerCase()}`;
      case 'duplicate':
        return `${this.getAction('duplicate')} ${entity.toLowerCase()}`;
      default:
        return isEditing 
          ? `${this.getAction('edit')} ${entity.toLowerCase()}`
          : `${this.getAction('create')} ${entity.toLowerCase()}`;
    }
  }

  /**
   * Génère une étiquette complète (icône + texte)
   */
  static getFullLabel(action: ActionType, options: ButtonLabelOptions = {}): { icon: string; text: string } {
    return {
      icon: this.getButtonIcon(action),
      text: this.getButtonLabel(action, options)
    };
  }
}

// Exports de convenance
export const Labels = LabelService;
export const L = LabelService; // Raccourci pour usage fréquent

// Exports des constantes pour usage direct
export const {
  ACTIONS,
  STATUS,
  LOADING,
  CONFIRMATION,
  ERRORS,
  PLACEHOLDERS,
  ENTITIES,
  NAVIGATION,
} = LabelService;

